import React from 'react';
import { Segment, Header, Button, Icon, Label, Message, Divider } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { UserComment } from '../../app/models/UserRatingModels';
import { useStore } from '../../app/stores/store';

interface ManageCommentsTabProps {
    comments: UserComment[];
    isMobile: boolean;
    totalCount?: number;
}

export default observer(function ManageCommentsTab({ 
    comments, 
    isMobile,
    totalCount 
}: ManageCommentsTabProps) {
    const { t } = useTranslation();
    const { userRatingStore } = useStore();
    const { updateCommentApproval } = userRatingStore;
    const totalComments = totalCount || comments.length;

    const handleToggleApproval = async (commentId: string, currentStatus: boolean) => {
        try {
            await updateCommentApproval(commentId, !currentStatus);
        } catch (error) {
            console.error('Failed to update comment approval:', error);
        }
    };

    // Calculate counts for summary
    const approvedComments = comments.filter(c => c.isApproved);
    const hiddenComments = comments.filter(c => !c.isApproved);

    return (
        <div style={{ padding: isMobile ? '5px' : '10px' }}>
            <Message info>
                <Message.Header>{t('manageComments.title')}</Message.Header>
                <p>{t('manageComments.description')}</p>
                
                {/* Comment Summary */}
                {totalComments > 0 && (
                    <div style={{ 
                        marginTop: '10px', 
                        padding: '10px', 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '5px',
                        border: '1px solid #e9ecef'
                    }}>
                        <Header as='h5' style={{ marginBottom: '5px' }}>
                            <Icon name='info circle' />
                            {t('manageComments.summary.title')}
                        </Header>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '10px'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2185d0' }}>
                                    {totalComments}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                    {t('manageComments.summary.total')}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#21ba45' }}>
                                    {approvedComments.length}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                    {t('manageComments.summary.approved')}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#db2828' }}>
                                    {hiddenComments.length}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                    {t('manageComments.summary.hidden')}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Message>

            {totalComments === 0 ? (
                <Message info>
                    <Message.Header>{t('manageComments.noComments.title')}</Message.Header>
                    <p>{t('manageComments.noComments.description')}</p>
                </Message>
            ) : (
                <>
                    {/* Approved Comments Section */}
                    {approvedComments.length > 0 && (
                        <>
                            <Header as='h3' color='green' dividing>
                                <Icon name='eye' />
                                {t('manageComments.approvedSection.count', { count: approvedComments.length })}
                                <Header.Subheader>
                                    {t('manageComments.approvedSection.subtitle')}
                                </Header.Subheader>
                            </Header>
                            
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: isMobile ? '10px' : '15px',
                                marginBottom: '20px'
                            }}>
                                {approvedComments
                                    .slice()
                                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                    .map(comment => (
                                        <Segment 
                                            key={comment.id}
                                            style={{
                                                border: '1px solid #21ba45',
                                                backgroundColor: '#f8fff8'
                                            }}
                                        >
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'flex-start',
                                                flexDirection: isMobile ? 'column' : 'row',
                                                gap: isMobile ? '10px' : '0'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <Header as='h5' style={{ marginBottom: '5px' }}>
                                                        <Icon name='user secret' />
                                                        {t('manageComments.approvedSection.anonymousUser')}
                                                        <Label 
                                                            size='mini' 
                                                            color='green'
                                                            style={{ marginLeft: '10px' }}
                                                        >
                                                            {t('manageComments.approvedSection.approvedLabel')}
                                                        </Label>
                                                        <Label 
                                                            size='mini' 
                                                            style={{ marginLeft: '5px', backgroundColor: '#21ba45', color: 'white' }}
                                                        >
                                                            <Icon name='eye' /> {t('manageComments.approvedSection.visibleLabel')}
                                                        </Label>
                                                    </Header>
                                                    <p style={{ 
                                                        fontSize: '0.85rem', 
                                                        color: '#999', 
                                                        marginBottom: '10px' 
                                                    }}>
                                                        {new Date(comment.createdAt).toLocaleDateString()}
                                                    </p>
                                                    <p style={{ 
                                                        fontSize: isMobile ? '0.9rem' : '1rem',
                                                        lineHeight: '1.5',
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                        {comment.comment}
                                                    </p>
                                                </div>
                                                
                                                <Button
                                                    size='small'
                                                    color='red'
                                                    onClick={() => handleToggleApproval(comment.id, comment.isApproved)}
                                                    style={{ flexShrink: 0 }}
                                                >
                                                    <Icon name='eye slash' />
                                                    {t('manageComments.approvedSection.hideButton')}
                                                </Button>
                                            </div>
                                        </Segment>
                                    ))}
                            </div>
                        </>
                    )}

                    {/* Hidden Comments Section */}
                    {hiddenComments.length > 0 && (
                        <>
                            {approvedComments.length > 0 && <Divider />}
                            
                            <Header as='h3' color='red' dividing>
                                <Icon name='eye slash' />
                                {t('manageComments.hiddenSection.count', { count: hiddenComments.length })}
                                <Header.Subheader>
                                    {t('manageComments.hiddenSection.subtitle')}
                                </Header.Subheader>
                            </Header>
                            
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: isMobile ? '10px' : '15px'
                            }}>
                                {hiddenComments
                                    .slice()
                                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                    .map(comment => (
                                        <Segment 
                                            key={comment.id}
                                            style={{
                                                border: '1px solid #db2828',
                                                backgroundColor: '#fff8f8'
                                            }}
                                        >
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'flex-start',
                                                flexDirection: isMobile ? 'column' : 'row',
                                                gap: isMobile ? '10px' : '0'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <Header as='h5' style={{ marginBottom: '5px' }}>
                                                        <Icon name='user secret' />
                                                        {t('manageComments.hiddenSection.anonymousUser')}
                                                        <Label 
                                                            size='mini' 
                                                            color='red'
                                                            style={{ marginLeft: '10px' }}
                                                        >
                                                            {t('manageComments.hiddenSection.hiddenLabel')}
                                                        </Label>
                                                        <Label 
                                                            size='mini' 
                                                            style={{ marginLeft: '5px', backgroundColor: '#db2828', color: 'white' }}
                                                        >
                                                            <Icon name='eye slash' /> {t('manageComments.hiddenSection.onlyYouLabel')}
                                                        </Label>
                                                    </Header>
                                                    <p style={{ 
                                                        fontSize: '0.85rem', 
                                                        color: '#999', 
                                                        marginBottom: '10px' 
                                                    }}>
                                                        {new Date(comment.createdAt).toLocaleDateString()}
                                                    </p>
                                                    <p style={{ 
                                                        fontSize: isMobile ? '0.9rem' : '1rem',
                                                        lineHeight: '1.5',
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                        {comment.comment}
                                                    </p>
                                                </div>
                                                
                                                <Button
                                                    size='small'
                                                    color='green'
                                                    onClick={() => handleToggleApproval(comment.id, comment.isApproved)}
                                                    style={{ flexShrink: 0 }}
                                                >
                                                    <Icon name='eye' />
                                                    {t('manageComments.hiddenSection.showButton')}
                                                </Button>
                                            </div>
                                        </Segment>
                                    ))}
                            </div>
                        </>
                    )}

                    {/* Empty States */}
                    {approvedComments.length === 0 && hiddenComments.length === 0 && (
                        <Message info>
                            <Message.Header>{t('manageComments.emptyStates.noCommentsToManage.title')}</Message.Header>
                            <p>{t('manageComments.emptyStates.noCommentsToManage.description')}</p>
                        </Message>
                    )}
                    
                    {approvedComments.length === 0 && hiddenComments.length > 0 && (
                        <Message warning>
                            <Message.Header>{t('manageComments.emptyStates.allHidden.title')}</Message.Header>
                            <p>{t('manageComments.emptyStates.allHidden.description')}</p>
                        </Message>
                    )}
                    
                    {hiddenComments.length === 0 && approvedComments.length > 0 && (
                        <Message success>
                            <Message.Header>{t('manageComments.emptyStates.allApproved.title')}</Message.Header>
                            <p>{t('manageComments.emptyStates.allApproved.description')}</p>
                        </Message>
                    )}
                </>
            )}
        </div>
    );
});